import { Badge } from "@/components/ui/shadcn/Badge/Badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/shadcn/Tabs/Tabs";
import { MatterTreatmentBillsCard } from "../MatterTreatmentBillsCard/MatterTreatmentBillsCard";
import { MatterTreatmentMedicalRecordsCard } from "../MatterTreatmentMedicalRecordsCard/MatterTreatmentMedicalRecordsCard";
import type { MedicalTreatmentBillViewModel } from "@/hooks/matter-view-models/use-medical-treatment-bill-view-model";
import type { TreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { type Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { ChevronRight } from "lucide-react";
import { type Dispatch, type ReactNode, type SetStateAction } from "react";

interface MatterTreatmentRecordsCardProps {
  records: TreatmentViewModel["records"];
  bills: MedicalTreatmentBillViewModel[];
  expanded: "records" | "none";
  setExpanded: Dispatch<SetStateAction<"records" | "none">>;
  contentClassName?: string;
  extraHeaderContent?: ReactNode;
  leftButtons?: ReactNode;
  rightButtons?: ReactNode;
  matter: Matter;
  showBillingSection?: boolean;
  showMedicalSection?: boolean;
}

export function MatterTreatmentRecordsCard({
  records,
  bills,
  expanded,
  setExpanded,
  extraHeaderContent,
  leftButtons,
  rightButtons,
  matter,
  showBillingSection = true,
  showMedicalSection = true,
}: MatterTreatmentRecordsCardProps) {
  const isExpanded = expanded === "records";

  const totalRecordCount =
    (showBillingSection ? bills.length : 0) +
    (showMedicalSection ? records.length : 0);

  const defaultTab = showBillingSection ? "bills" : "medical";

  return (
    <div className="border-border bg-card overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={() => {
          setExpanded((prev) => (prev === "records" ? "none" : "records"));
        }}
        className="hover:bg-muted/50 flex w-full items-center gap-2 px-4 py-3 transition-colors"
      >
        <ChevronRight
          className={cn(
            "text-muted-foreground h-4 w-4 shrink-0 transition-transform",
            isExpanded && "rotate-90",
          )}
        />
        <span className="text-sm font-semibold">Records</span>
        <Badge variant="secondary" className="text-xs">
          {totalRecordCount} {totalRecordCount === 1 ? "record" : "records"}
        </Badge>
        {leftButtons}
        <div className="text-muted-foreground ml-auto flex items-center gap-4 text-xs">
          {rightButtons}
          {extraHeaderContent}
        </div>
      </button>

      {isExpanded && (
        <div>
          <Tabs defaultValue={defaultTab}>
            <TabsList className="h-auto w-full justify-start rounded-none border-b bg-transparent p-0">
              {showBillingSection && (
                <TabsTrigger
                  value="bills"
                  className="text-muted-foreground data-[state=active]:border-foreground data-[state=active]:bg-background data-[state=active]:text-foreground rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-normal shadow-none data-[state=active]:shadow-none"
                >
                  Bills ({bills.length})
                </TabsTrigger>
              )}
              {showMedicalSection && (
                <TabsTrigger
                  value="medical"
                  className="text-muted-foreground data-[state=active]:border-foreground data-[state=active]:bg-background data-[state=active]:text-foreground rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-normal shadow-none data-[state=active]:shadow-none"
                >
                  Medical records ({records.length})
                </TabsTrigger>
              )}
            </TabsList>

            {showBillingSection && (
              <TabsContent value="bills" className="mt-0">
                <MatterTreatmentBillsCard bills={bills} matter={matter} />
              </TabsContent>
            )}

            {showMedicalSection && (
              <TabsContent value="medical" className="mt-0">
                <MatterTreatmentMedicalRecordsCard
                  records={records}
                  matter={matter}
                />
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}
    </div>
  );
}
